import { Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { GlobalContext } from "../context/GlobalContext.tsx";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getUserAttributes } from "../utils/auth.ts";
import useGlobalContext from "../context/useGlobalContext.ts";
import AnalyticsIcon from "../assets/analytics/analytics-icon.svg";
import "../styles/analytics.css";
import RightArrowIcon from "../assets/common/right-arrow-icon.svg";
import AnalyticsTasks from "../components/Analytics/AnalyticsTasks.tsx";
import AppNotification from "../components/Common/AppNotification.tsx";
import TaskNotifications from "../components/ActionItems/TaskNotifications.tsx";
import { useNotificationCount } from "../hooks/useNotificationCount.ts";
import UserTeam from "../components/Analytics/UserTeam.tsx";
import { DateRangeValue, UserInfo, UserTeamData } from "../utils/types.ts";
import FilterPill from "../components/Common/FilterPill.tsx";
import { format } from "date-fns";
import InteractionsAnalytics from "../components/Analytics/InteractionsAnalytics.tsx";
import Analytics from "../components/Analytics/Analytics.tsx";
import { getPresetDateRange, interactionTypeOptions } from "../utils/utils.ts";
import InfoIcon from "../assets/common/info_icon.svg";
import ClientAsk7G from "../components/ClientOverview/ClientAsk7G.tsx";
import MMNAReport, { SavedViewsReportState } from "../components/Analytics/MMNAReport.tsx";
import SaveViewPreferences from "../components/Analytics/SaveViewPreferences.tsx";

const AnalyticsPage = () => {
  const { featureMap, accountDetails } = useContext(GlobalContext);
  const loggedInEmail = getUserAttributes()?.email;
  const location = useLocation();
  const pathParts = location.pathname.split("/");
  const lastPart = pathParts[pathParts.length - 1];
  const isBusinessPage = lastPart === "business";
  const [selectedTab, setSelectedTab] = useState<
    "my-analytics" | "team-analytics" | "account-analytics" | "table-format"
  >("my-analytics");
  const { userTeamData } = useGlobalContext();
  const analyticsLabelMap: Record<string, string> = {
    interactions: "Interactions",
    tasks: "Tasks",
    business: "Business",
    report: "Pipeline Dashboard",
  };
  const [interactionType, setInteractionType] = useState<string>( location?.state?.interactionType || "")
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const isNotificationsEnabled = featureMap?.in_app_notifications === true;
  const userNotification = useNotificationCount(
    loggedInProfileOrgId,
    isNotificationsEnabled
  );
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [newUserTeamData, setNewUserTeamData] = useState<UserTeamData | null>(
    null
  );
  const [analyticsDateRange, setAnalyticsDateRange] = useState<DateRangeValue>({
    preset: "currentWeek",
    startDate: "",
    endDate: "",
  });
  const businessLastThreeMonths = useMemo(
    () => getPresetDateRange("last3MonthsFull"),
    []
  );
  const isAnalyticsTab = (
    value: unknown
  ): value is "my-analytics" | "team-analytics" | "account-analytics" | "table-format" =>
    value === "my-analytics" ||
    value === "team-analytics" ||
    value === "account-analytics" ||
    value === "table-format";
  const isCallQualityDashboardEnabled = featureMap?.call_quality_dashboard === true;
  const isBusinessAnalyticsEnabled = featureMap?.bussiness_analytics === true;
  const isTasksAnalyticsEnabled = featureMap?.task_analytics === true;
  const isInteractiontypeFilterEnabled = featureMap?.interaction_type_filter === true;
  const isInteractionAnalyticsAccountTabEnabled = featureMap?.interaction_analytics_account_tab === true;
  const Ask7GEnabled = featureMap?.business_ask7g === true;
  const isPipeLineReportEnabled = featureMap?.report_analytics === true;
  const [savedViewsState, setSavedViewsState] = useState<SavedViewsReportState | null>(null);
  const [isAsk7GExpanded, setIsAsk7GExpanded] = useState(false);

  useEffect(() => {
    setIsAsk7GExpanded(false);
  }, [location.pathname, location.search, selectedTab]);
  const handleNewUserTeamData = (data: {
    currentUser: string;
    clickedUserInfo?: UserInfo;
    currentManager?: UserInfo;
    departments: any[];
    subordinates: UserInfo[];
    managerOptions: UserInfo[];
  }) => {
    setNewUserTeamData(data);
  };

  useEffect(() => {
    if (lastPart === "report") {
      setSelectedTab("table-format");
      return;
    }
    const tabFromRouteState =
      location.state?.selectedAnalyticsTab ??
      location.state?.selectedTab ??
      location.state?.selectedTabs;

    if (isAnalyticsTab(tabFromRouteState)) {
      setSelectedTab(tabFromRouteState);
    } else {
      setSelectedTab("my-analytics");
    }
  }, [location.pathname, location.state, lastPart]);

  useEffect(() => {
    if (isBusinessPage) return;

    const restoredDateRange = location.state?.analyticsDateRange;
    if (
      restoredDateRange?.startDate &&
      restoredDateRange?.endDate &&
      restoredDateRange?.preset
    ) {
      setAnalyticsDateRange(restoredDateRange);
    }
  }, [location.state, isBusinessPage]);

  useEffect(() => {
    if (!isBusinessPage) return;

    setAnalyticsDateRange((prev) => {
      if (
        prev?.preset === "custom" &&
        prev?.startDate === businessLastThreeMonths.start_time &&
        prev?.endDate === businessLastThreeMonths.end_time
      ) {
        return prev;
      }

      return {
        preset: "custom",
        startDate: businessLastThreeMonths.start_time,
        endDate: businessLastThreeMonths.end_time,
      };
    });
  }, [isBusinessPage, businessLastThreeMonths.start_time, businessLastThreeMonths.end_time]);

  return (
    <Box id="analyticsParent">
      <Box className="analytics__header">
        <Box
          className="interaction-breadcrumbs"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            component={isAsk7GExpanded ? "button" : "div"}
            type={isAsk7GExpanded ? "button" : undefined}
            className={`flex flex-row justify-start items-center gap-2 ask7g-home-breadcrumb ${
              isAsk7GExpanded ? "ask7g-home-breadcrumb--expanded" : "ask7g-home-breadcrumb--collapsed"
            }`}
            onClick={isAsk7GExpanded ? () => setIsAsk7GExpanded(false) : undefined}
          >
          <img src={AnalyticsIcon} alt="" height={16} width={16} />
          <h2>Analytics</h2>
          </Box>

          {analyticsLabelMap[lastPart] && !isAsk7GExpanded && (
            <>
              <img src={RightArrowIcon} alt="" style={{ marginTop: "3px" }} />
              <h1 className="interactions-title" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                {analyticsLabelMap[lastPart]}
                <span className="interactions-date-range">
                  {lastPart !== "report" && (
                    analyticsDateRange?.overdue ? (
                      <> {" "} (As of today)</>
                    ) : (
                      analyticsDateRange?.startDate &&
                      analyticsDateRange?.endDate && (
                        <>
                          {" "}
                          (
                          {format(new Date(analyticsDateRange.startDate), "MMM dd yyyy")} -{" "}
                          {format(new Date(analyticsDateRange.endDate), "MMM dd yyyy")})
                        </>
                      )
                    )
                  )}
                </span>
                {lastPart === "interactions" && (
                    <Tooltip title= "Date range is limited up to today. Future dates are excluded." arrow>
                        <img src={InfoIcon} alt="info" className="interactions_info-icon" />
                    </Tooltip>
                  )}
              </h1>
            </>
          )}
          {Ask7GEnabled && isAsk7GExpanded && (
            <>
              <img src={RightArrowIcon} alt="" style={{marginTop: "3px"}} />
              <Typography className="breadCrumbs-text ask7g-current-breadcrumb" sx={{fontSize: "16px"}}>
                Ask 7G
              </Typography>
            </>
          )}
        </Box>
        <div className="ml-auto flex flex-row items-center gap-3">
        {isPipeLineReportEnabled && lastPart === "report" && savedViewsState && !isAsk7GExpanded && (
          <SaveViewPreferences
            salesforceReportPref={savedViewsState.salesforceReportPref}
            activeFilterCount={savedViewsState.activeFilterCount}
            visibleColumnCount={savedViewsState.visibleColumnCount}
            hasUnsavedChanges={savedViewsState.hasUnsavedChanges}
            activeViewName={savedViewsState.activeViewName}
            currentConfigSnapshot={savedViewsState.currentConfigSnapshot}
            onSelectView={savedViewsState.onSelectView}
            onClearAllFilters={savedViewsState.onClearAllFilters}
          />
        )}
        {isNotificationsEnabled && (
          <AppNotification
            notificationCount={userNotification.notificationCount}
            notificationApiRefetch={
              userNotification.getNotificationCountRefetch
            }
            contentComponent={
              <TaskNotifications userNotification={userNotification} />
            }
          />
        )}
        {Ask7GEnabled && (
          <ClientAsk7G
            view="icon"
            isExpanded={isAsk7GExpanded}
            onToggleExpand={setIsAsk7GExpanded}
          />
        )}
        </div>
      </Box>
      {Ask7GEnabled && (
        <Box sx={{ width: "100%", display: isAsk7GExpanded ? "block" : "none" }}>
          <ClientAsk7G
            view="full"
            isExpanded={isAsk7GExpanded}
            onToggleExpand={setIsAsk7GExpanded}
          />
        </Box>
      )}
      <Box sx={{ display: isAsk7GExpanded ? "none" : "block", width: "100%" }}>
      {lastPart !== "report" && (
        <Box className="analytics__user-team-filter">
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FilterPill
            key="meeting-date"
            selectedTab={selectedTab}
            type="date"
            label="Time Period"
            value={analyticsDateRange}
            onChange={(val) => {
              if (
                val &&
                typeof val === "object" &&
                !Array.isArray(val) &&
                "preset" in val
              ) {
                setAnalyticsDateRange({
                  preset: val.preset,
                  startDate: val.startDate,
                  endDate: val.endDate,
                });
              } else if (typeof val === "string") {
                setAnalyticsDateRange((prev) => ({
                  ...prev,
                  preset: val || "",
                }));
              }
            }}
            showClearIcon={false}
            {
              ...(lastPart === "interactions" ? {limitFutureDays: 0} : {})
            }
          />
          {
            lastPart === "interactions" && isInteractiontypeFilterEnabled && (
              <FilterPill 
              label= "Interaction Type"
              selectedTab={selectedTab}
              type="select"
              value={interactionType}
              onChange={(val) => {
                if (typeof val === "string" || val === undefined) {
                  setInteractionType(val || "");
                }
            }}
              options={interactionTypeOptions}
              />
            )
          }
          </Box>

          {selectedTab === "team-analytics" && userTeamData.showTeamTab && (
            <UserTeam
              setSelectedUser={setSelectedUser}
              loggedInEmail={loggedInEmail}
              loggedInProfileOrgId={loggedInProfileOrgId}
              showDropdown={true}
              display={false}
              onDataUpdate={handleNewUserTeamData}
              parent_page="analytics-page"
            />
          )}
        </Box>
      )}
      <Box
        className={`analytics-Container ${
          lastPart === "report" ? "analytics-Container-report" : ""
        }`}
      >
        {lastPart !== "report" && (
          <Box
            className="flex justify-between w-full h-auto bg-[#F0F0F0]"
            sx={{
              height: "46px", // Adjust as needed
              borderBottom: "1px solid rgba(229, 229, 229, 1)",
              alignItems: "center", // Ensures vertical alignment
              display: "flex",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={(_event, newValue) => setSelectedTab(newValue)}
              className="app-tabs-container"
            >
              <Tab
                value="my-analytics"
                label={
                  <Typography className="analytics__tab-text">
                    My Analytics
                  </Typography>
                }
              />

              {userTeamData.showTeamTab && (
                <Tab
                  value="team-analytics"
                  label={
                    <Typography className="analytics__tab-text">
                      Team Analytics
                    </Typography>
                  }
                />
              )}

              {lastPart === "interactions" && isInteractionAnalyticsAccountTabEnabled && (
                <Tab
                  value="account-analytics"
                  label={
                    <Typography className="analytics__tab-text">
                      Account Analytics
                    </Typography>
                  }
                />
              )}
            </Tabs>
          </Box>
        )}
        <Box className="analytics-scroll-container">
        {lastPart === "interactions" &&
          isCallQualityDashboardEnabled && (
            <InteractionsAnalytics
              selectedTab={selectedTab as any}
              analyticsDateRange={analyticsDateRange}
              newUserTeamData={newUserTeamData}
              setAnalyticsDateRange={setAnalyticsDateRange}
              interactionType={interactionType}
            />
        )}
        {lastPart === "business" && isBusinessAnalyticsEnabled && (
          <Analytics
            selectedUser={selectedUser}
            selectedTab={selectedTab as any}
            newUserTeamData={newUserTeamData}
            analyticsDateRange={analyticsDateRange}
            setAnalyticsDateRange={setAnalyticsDateRange}
          />
        )}
        {lastPart === "tasks" && isTasksAnalyticsEnabled && (
        <AnalyticsTasks
        selectedTab={selectedTab as any}
        analyticsDateRange={analyticsDateRange}
        newUserTeamData={newUserTeamData}
        setAnalyticsDateRange={setAnalyticsDateRange}
        />)}
        {lastPart === "report" && (
          <MMNAReport onReportStateChange={setSavedViewsState} />
        )}
        </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default AnalyticsPage;
